import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { getData as getRequirements } from "../../models/RequirementsModel";
import { createFromResume } from "../../models/CandidatesModel";
import { isResumeConverted } from "../../helpers/ResumeToCandidateHelper";

export default function BulkCreateCandidatesFromResumeModal({
  show,
  onClose,
  resumes = [],
  clients = [],
  onComplete,
}) {
  const { state } = useAuth();
  const user_token = state?.user_data?.user_token;

  const [selectedClient, setSelectedClient] = useState("");
  const [selectedRequirement, setSelectedRequirement] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("Immediate");
  const [experienceTotal, setExperienceTotal] = useState("0");
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState("");

  const eligibleResumes = resumes.filter((row) => !isResumeConverted(row));

  useEffect(() => {
    if (!show) {
      setSelectedClient("");
      setSelectedRequirement("");
      setNoticePeriod("Immediate");
      setExperienceTotal("0");
      setFilteredRequirements([]);
      setProgress("");
      setSubmitting(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !selectedClient || !user_token) {
      setFilteredRequirements([]);
      setSelectedRequirement("");
      return;
    }

    let cancelled = false;

    const fetchClientRequirements = async () => {
      setLoadingRequirements(true);
      setSelectedRequirement("");

      try {
        const response = await getRequirements({
          user_token,
          requirement_client_id: selectedClient,
          page: 1,
          limit: -1,
        });

        if (cancelled) return;

        setFilteredRequirements(
          (response?.requirements || []).map((item) => ({
            value: item._requirement_id,
            label: item.requirement_name,
          }))
        );
      } catch (error) {
        console.error(error);
        if (!cancelled) setFilteredRequirements([]);
      } finally {
        if (!cancelled) setLoadingRequirements(false);
      }
    };

    fetchClientRequirements();

    return () => {
      cancelled = true;
    };
  }, [selectedClient, show, user_token]);

  const handleConfirm = async () => {
    if (!selectedClient || !selectedRequirement || !eligibleResumes.length) return;

    setSubmitting(true);
    let created = 0;
    let skipped = resumes.length - eligibleResumes.length;
    let failed = 0;

    for (let index = 0; index < eligibleResumes.length; index += 1) {
      const resume = eligibleResumes[index];
      setProgress(
        `Creating ${index + 1} of ${eligibleResumes.length}: ${resume.resume_name || "Unnamed"}`
      );

      try {
        const response = await createFromResume(user_token, resume._resume_id, {
          candidate_client_id: selectedClient,
          candidate_requirement_id: selectedRequirement,
          candidate_notice_period: noticePeriod,
          candidate_experience_total: experienceTotal,
        });

        if (response?.status) {
          created += 1;
        } else {
          failed += 1;
        }
      } catch (error) {
        console.error(error);
        failed += 1;
      }
    }

    setSubmitting(false);
    setProgress("");

    if (created > 0) {
      toast.success(`${created} candidate(s) created from resumes.`);
    }
    if (skipped > 0) {
      toast.info(`${skipped} resume(s) skipped — already converted.`);
    }
    if (failed > 0) {
      toast.warning(`${failed} resume(s) could not be converted.`);
    }

    onComplete?.({ created, skipped, failed });
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Bulk create candidates from resumes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          {eligibleResumes.length} of {resumes.length} selected resume(s) can be converted.
          Already converted rows are skipped automatically.
        </p>

        <div className="mb-3">
          <Form.Label>
            Client <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(event) => setSelectedClient(event.target.value)}
            disabled={submitting}
          >
            <option value="">Select client</option>
            {clients.map((client) => (
              <option key={client.value} value={String(client.value)}>
                {client.label}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="mb-3">
          <Form.Label>
            Requirement <span className="text-danger">*</span>
          </Form.Label>
          {loadingRequirements ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <Spinner animation="border" size="sm" />
              <span>Loading requirements…</span>
            </div>
          ) : (
            <Form.Select
              value={selectedRequirement}
              onChange={(event) => setSelectedRequirement(event.target.value)}
              disabled={!selectedClient || submitting}
            >
              <option value="">Select requirement</option>
              {filteredRequirements.map((req) => (
                <option key={req.value} value={String(req.value)}>
                  {req.label}
                </option>
              ))}
            </Form.Select>
          )}
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <Form.Label>Notice period</Form.Label>
            <Form.Control
              value={noticePeriod}
              onChange={(event) => setNoticePeriod(event.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="col-md-6">
            <Form.Label>Total experience (years)</Form.Label>
            <Form.Control
              value={experienceTotal}
              onChange={(event) => setExperienceTotal(event.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        {progress && (
          <div className="text-primary small mt-3">
            <Spinner animation="border" size="sm" className="me-2" />
            {progress}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={
            submitting ||
            !selectedClient ||
            !selectedRequirement ||
            eligibleResumes.length === 0
          }
        >
          {submitting ? "Creating…" : `Create ${eligibleResumes.length} candidate(s)`}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
